import { Controller } from "@hotwired/stimulus"
import { createHereMap, disposeHereMap } from "../helpers/here_map_helpers"
import {
  circleToPolygon,
  midpoint,
  polygonArea,
  round,
  selfIntersecting,
} from "../helpers/geofence_geometry"

// The map editor for a site's geofence (docs/plans/vehicle-geofence-auto-booking.md), in two
// shapes:
//
// Kreis   — the circle follows the radius (typed, slid or picked from the presets), dragging
//           the centre marker moves it and writes the hidden centre fields.
// Fläche  — a polygon. Switching from the circle starts from an octagon on it; "Neu zeichnen"
//           starts from nothing, and every tap on the map adds a corner until the shape is
//           closed (tap the first corner, double-tap, or "Fläche schließen"). Corners are
//           dragged, a tap on an edge's midpoint inserts one, a right-click (long press)
//           removes one. The corners go to the hidden `coordinates` field as JSON; the server
//           validates them again (SiteGeofence).
//
// Recent vehicle positions around the site may be shown as dots, as a drawing aid: where the
// machines actually stood is usually the best guide to where the area ends.
export const CIRCLE_STYLE = {
  strokeColor: "rgba(22, 163, 74, 0.9)",
  fillColor: "rgba(22, 163, 74, 0.15)",
  lineWidth: 2,
}
const POSITION_STYLE = { strokeColor: "rgba(37, 99, 235, 0)", fillColor: "rgba(37, 99, 235, 0.45)" }
const POSITION_RADIUS_M = 1.5
const MAX_VERTICES = 100
const MIDPOINT_REACH_PX = 12

const FIT_PADDING = 48

const VERTEX_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16">
  <circle cx="8" cy="8" r="6" fill="#ffffff" stroke="#16a34a" stroke-width="2.5"/></svg>`
const FIRST_VERTEX_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18">
  <circle cx="9" cy="9" r="7" fill="#16a34a" stroke="#ffffff" stroke-width="2.5"/></svg>`
const MIDPOINT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12">
  <circle cx="6" cy="6" r="4" fill="rgba(255,255,255,0.85)" stroke="#16a34a" stroke-width="1.5"/></svg>`

export default class extends Controller {
  static targets = [
    "map",
    "latitude",
    "longitude",
    "radius",
    "slider",
    "preset",
    "diameter",
    "area",
    "shape",
    "shapeOption",
    "coordinates",
    "circlePanel",
    "polygonPanel",
    "vertexCount",
    "polygonArea",
    "polygonWarning",
    "drawingHint",
    "closeButton",
    "undoButton",
  ]
  static values = { siteLatitude: Number, siteLongitude: Number, positions: Array }

  connect() {
    this.radiusEditable = this.hasRadiusTarget && !this.radiusTarget.disabled
    this.points = this.storedPoints()
    this.closed = this.points.length >= 3
    this.refreshReadouts()
    this.showShapePanels()
    const connection = (this.connection = (this.connection || 0) + 1)
    createHereMap(this.mapTarget, { center: this.center(), zoom: 16 })
      .then((ctx) => {
        // Disconnected (or reconnected) while HERE was still loading: nothing would ever
        // dispose this map.
        if (connection === this.connection) this.initializeMap(ctx)
        else disposeHereMap(ctx.map)
      })
      .catch((error) => console.error("HERE map failed to load", error))
  }

  disconnect() {
    this.connection += 1
    disposeHereMap(this.map)
    this.map = null
    this.circle = null
    this.marker = null
    this.polygonGroup = null
    this.outline = null
    this.midpointMarkers = []
    this.vertexMarkers = []
    this.positionsGroup = null
  }

  initializeMap({ H, map, behavior }) {
    this.H = H
    this.map = map
    this.behavior = behavior

    this.circle = new H.map.Circle(this.center(), this.radius(), { style: CIRCLE_STYLE })
    this.marker = new H.map.Marker(this.center(), { volatility: true })
    this.marker.draggable = true
    // Room around the shape when the view fits it, so its edge never sits on the map border.
    map.getViewPort?.().setPadding(FIT_PADDING, FIT_PADDING, FIT_PADDING, FIT_PADDING)
    this.showPositions(this.positionsValue.length > 0)
    this.setupDragging()
    this.setupPolygonEvents()
    this.showShapeOnMap()
    this.fitToShape()
  }

  // ---- Shape ------------------------------------------------------------------------------

  shape() {
    return this.hasShapeTarget ? this.shapeTarget.value : "circle"
  }

  // data-action="change->geofence-editor#shapeChanged" on the Kreis/Fläche radios.
  shapeChanged(event) {
    this.shapeTarget.value = event.target.value
    // An octagon nobody has edited yet follows the circle; a drawn area is kept.
    if (this.shape() === "polygon" && (this.points.length === 0 || this.pointsFromCircle)) {
      this.points = circleToPolygon(this.center(), this.radius())
      this.closed = true
      this.pointsFromCircle = true
    }
    this.writeCoordinates()
    this.showShapePanels()
    this.showShapeOnMap()
    this.refreshReadouts()
    this.fitToShape()
  }

  showShapePanels() {
    const polygon = this.shape() === "polygon"
    if (this.hasCirclePanelTarget) this.circlePanelTarget.hidden = polygon
    if (this.hasPolygonPanelTarget) this.polygonPanelTarget.hidden = !polygon
    // A hidden radius out of its min/max would block the browser's form validation without
    // anything visible to fix; a polygon carries no radius anyway.
    ;[this.hasRadiusTarget && this.radiusTarget, this.hasSliderTarget && this.sliderTarget]
      .filter(Boolean)
      .forEach((input) => {
        input.disabled = polygon || !this.radiusEditable
      })
    this.shapeOptionTargets.forEach((option) => {
      option.checked = option.value === this.shape()
    })
  }

  showShapeOnMap() {
    if (!this.map) return
    const polygon = this.shape() === "polygon"
    this.toggleObject(this.circle, !polygon)
    this.toggleObject(this.marker, !polygon)
    if (polygon) this.renderPolygon()
    else this.removePolygon()
    this.updateDoubleTapZoom()
  }

  toggleObject(object, visible) {
    const shown = this.map.getObjects?.().includes(object) ?? object.__shown
    if (visible && !shown) this.map.addObject(object)
    if (!visible && shown) this.map.removeObject(object)
    object.__shown = visible
  }

  fitToShape() {
    if (this.shape() === "polygon") this.fitToPolygon()
    else this.fitToCircle()
  }

  // ---- Circle -----------------------------------------------------------------------------

  // data-action="input->geofence-editor#radiusChanged"
  radiusChanged() {
    if (this.hasSliderTarget) this.sliderTarget.value = this.radius()
    this.redraw()
  }

  // The slider covers the common range only; the number field takes larger radii.
  sliderChanged() {
    this.radiusTarget.value = this.sliderTarget.value
    this.radiusChanged()
  }

  // data-action="geofence-editor#presetRadius" data-geofence-editor-radius-param="250"
  presetRadius({ params: { radius } }) {
    this.radiusTarget.value = radius
    this.radiusChanged()
  }

  redraw() {
    this.refreshReadouts()
    if (!this.circle) return
    this.circle.setRadius(this.radius())
    this.fitToCircle()
  }

  refreshReadouts() {
    const format = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 })
    const radius = this.radius()
    if (this.hasDiameterTarget) this.diameterTarget.textContent = `${format.format(radius * 2)} m`
    if (this.hasAreaTarget)
      this.areaTarget.textContent = formatArea(Math.PI * radius * radius, format)
    this.presetTargets.forEach((preset) => {
      preset.dataset.active = String(Number(preset.dataset.radius) === radius)
    })
    this.refreshPolygonReadouts(format)
  }

  // Back to the site's geocoded address.
  resetCenter() {
    if (!this.hasSiteLatitudeValue || !this.hasSiteLongitudeValue) return
    this.moveCenter({ lat: this.siteLatitudeValue, lng: this.siteLongitudeValue })
    this.fitToCircle()
  }

  center() {
    return {
      lat: parseFloat(this.latitudeTarget.value),
      lng: parseFloat(this.longitudeTarget.value),
    }
  }

  // An empty or partly typed radius keeps the last drawn circle instead of collapsing it.
  radius() {
    const value = parseInt(this.radiusTarget.value, 10)
    if (Number.isFinite(value) && value > 0) this.lastRadius = value
    return this.lastRadius || 0
  }

  moveCenter(position) {
    this.latitudeTarget.value = position.lat.toFixed(7)
    this.longitudeTarget.value = position.lng.toFixed(7)
    if (this.circle) this.circle.setCenter(position)
    if (this.marker) this.marker.setGeometry(position)
  }

  // A no-op until the map has loaded (or when it failed to).
  fitToCircle() {
    if (!this.map || !this.circle || this.radius() <= 0) return
    this.map.getViewModel().setLookAtData({ bounds: this.circle.getBoundingBox() }, false)
  }

  // ---- Polygon ----------------------------------------------------------------------------

  storedPoints() {
    if (!this.hasCoordinatesTarget || !this.coordinatesTarget.value) return []
    try {
      return JSON.parse(this.coordinatesTarget.value)
        .filter((pair) => Array.isArray(pair) && pair.length === 2)
        .map(([lat, lng]) => ({ lat: Number(lat), lng: Number(lng) }))
    } catch {
      return []
    }
  }

  writeCoordinates() {
    if (!this.hasCoordinatesTarget) return
    this.coordinatesTarget.value = JSON.stringify(
      this.points.map(({ lat, lng }) => [round(lat), round(lng)]),
    )
  }

  // "Neu zeichnen": every tap on the map now adds a corner.
  startDrawing() {
    this.points = []
    this.closed = false
    this.submitAttempted = false
    this.polygonChanged()
  }

  // "Letzten Punkt entfernen" while drawing.
  undoPoint() {
    this.points.pop()
    this.closed = false
    this.polygonChanged()
  }

  // "Fläche schließen"
  closePolygon() {
    if (this.points.length < 3) return
    this.closed = true
    this.polygonChanged()
    this.fitToPolygon()
  }

  addPoint(position) {
    if (this.closed || this.points.length >= MAX_VERTICES) return
    this.points.push({ lat: round(position.lat), lng: round(position.lng) })
    this.polygonChanged()
  }

  insertPoint(afterIndex) {
    if (this.points.length >= MAX_VERTICES) return
    const next = this.points[(afterIndex + 1) % this.points.length]
    this.points.splice(afterIndex + 1, 0, midpoint(this.points[afterIndex], next))
    this.polygonChanged()
  }

  // A closed polygon keeps at least three corners.
  removePoint(index) {
    if (this.closed && this.points.length <= 3) return
    this.points.splice(index, 1)
    this.polygonChanged()
  }

  movePoint(index, position) {
    this.pointsFromCircle = false
    this.points[index] = { lat: round(position.lat), lng: round(position.lng) }
    this.writeCoordinates()
    this.refreshReadouts()
    this.renderPolygon({ keepVertices: true })
  }

  polygonChanged() {
    this.pointsFromCircle = false
    this.writeCoordinates()
    this.refreshReadouts()
    this.renderPolygon()
    this.updateDoubleTapZoom()
  }

  refreshPolygonReadouts(format) {
    const count = this.points.length
    if (this.hasVertexCountTarget) this.vertexCountTarget.textContent = format.format(count)
    if (this.hasPolygonAreaTarget)
      this.polygonAreaTarget.textContent = formatArea(polygonArea(this.points), format)
    if (this.hasPolygonWarningTarget) {
      const warning = this.polygonWarning(count)
      this.polygonWarningTarget.hidden = !warning
      if (warning)
        this.polygonWarningTarget.textContent = this.polygonWarningTarget.dataset[warning]
    }
    if (this.hasDrawingHintTarget) this.drawingHintTarget.hidden = this.closed
    if (this.hasCloseButtonTarget) this.closeButtonTarget.disabled = this.closed || count < 3
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = this.closed || count === 0
  }

  // The data-* key of the message to show, or null.
  polygonWarning(count) {
    if (count > 0 && count < 3) return "tooFew"
    if (selfIntersecting(this.points)) return "crossing"
    if (this.submitAttempted && !this.closed) return "notClosed"
    return null
  }

  // data-action="submit->geofence-editor#submit": an open drawing is not saved as an area the
  // server would close by itself — the closing edge may not be the one the user means.
  submit(event) {
    if (this.shape() !== "polygon" || this.closed) return
    event.preventDefault()
    this.submitAttempted = true
    this.refreshReadouts()
  }

  removePolygon() {
    if (this.polygonGroup) this.map.removeObject(this.polygonGroup)
    this.polygonGroup = null
    this.outline = null
    this.midpointMarkers = []
    this.vertexMarkers = []
  }

  // The shape, its corners and — once closed — a handle on every edge's midpoint. While a
  // corner is dragged only the outline and the handles are redrawn; recreating the dragged
  // marker would end the drag.
  renderPolygon({ keepVertices = false } = {}) {
    if (!this.map || this.shape() !== "polygon") return
    const { H } = this
    if (!this.polygonGroup) {
      this.polygonGroup = new H.map.Group()
      this.map.addObject(this.polygonGroup)
    }
    if (this.outline) this.polygonGroup.removeObject(this.outline)
    ;(this.midpointMarkers || []).forEach((marker) => this.polygonGroup.removeObject(marker))
    if (!keepVertices) {
      ;(this.vertexMarkers || []).forEach((marker) => this.polygonGroup.removeObject(marker))
      this.vertexMarkers = this.points.map((point, index) => this.vertexMarker(point, index))
      this.polygonGroup.addObjects(this.vertexMarkers)
    } else {
      this.vertexMarkers.forEach((marker, index) => marker.setGeometry(this.points[index]))
    }

    this.outline = this.outlineObject()
    if (this.outline) this.polygonGroup.addObject(this.outline)
    this.midpointMarkers = this.closed
      ? this.points.map((point, index) => this.midpointMarker(index))
      : []
    this.polygonGroup.addObjects(this.midpointMarkers)
  }

  outlineObject() {
    const { H } = this
    if (this.points.length < 2) return null
    const line = new H.geo.LineString()
    this.points.forEach((point) => line.pushPoint(point))
    if (this.closed && this.points.length >= 3) {
      return new H.map.Polygon(new H.geo.Polygon(line), { style: CIRCLE_STYLE, zIndex: 1 })
    }
    return new H.map.Polyline(line, {
      style: { strokeColor: CIRCLE_STYLE.strokeColor, lineWidth: 2, lineDash: [4, 4] },
    })
  }

  vertexMarker(point, index) {
    const { H } = this
    const svg = index === 0 && !this.closed ? FIRST_VERTEX_SVG : VERTEX_SVG
    const size = index === 0 && !this.closed ? 18 : 16
    const marker = new H.map.Marker(point, {
      icon: new H.map.Icon(svg, { anchor: { x: size / 2, y: size / 2 } }),
      volatility: true,
      zIndex: 3,
      data: { kind: "vertex", index },
    })
    marker.draggable = true
    return marker
  }

  midpointMarker(index) {
    const { H } = this
    const next = this.points[(index + 1) % this.points.length]
    return new H.map.Marker(midpoint(this.points[index], next), {
      icon: new H.map.Icon(MIDPOINT_SVG, { anchor: { x: 6, y: 6 } }),
      volatility: true,
      zIndex: 2,
      data: { kind: "midpoint", index },
    })
  }

  fitToPolygon() {
    if (!this.map || !this.outline || !this.closed) return
    this.map.getViewModel().setLookAtData({ bounds: this.outline.getBoundingBox() }, false)
  }

  // A double tap closes the shape while drawing, so it must not zoom then.
  updateDoubleTapZoom() {
    const feature = this.H?.mapevents?.Behavior?.Feature?.DBL_TAP_ZOOM
    if (!this.behavior || feature === undefined) return
    const drawing = this.shape() === "polygon" && !this.closed
    if (drawing) this.behavior.disable(feature)
    else this.behavior.enable(feature)
  }

  setupPolygonEvents() {
    this.map.addEventListener("tap", (event) => {
      if (this.shape() !== "polygon") return
      const data = event.target?.getData?.()
      if (data?.kind === "vertex") {
        if (data.index === 0 && !this.closed) this.closePolygon()
        return
      }
      const edge = data?.kind === "midpoint" ? data.index : this.midpointNear(event)
      if (edge !== null) return this.insertPoint(edge)
      // Anything else under the tap — the map, a position dot, the open line — places a corner.
      this.addPoint(this.geoAt(event))
    })
    this.map.addEventListener("dbltap", () => {
      if (this.shape() !== "polygon" || this.closed) return
      // The double tap's own taps added a corner on the same spot: drop it.
      const [previous, last] = this.points.slice(-2)
      if (
        previous &&
        last &&
        Math.abs(previous.lat - last.lat) + Math.abs(previous.lng - last.lng) < 1e-5
      )
        this.points.pop()
      this.closePolygon()
    })
    this.map.addEventListener("contextmenu", (event) => {
      const data = event.target?.getData?.()
      if (this.shape() === "polygon" && data?.kind === "vertex") this.removePoint(data.index)
    })
  }

  // The edge whose midpoint handle is within reach of the tap, or null. Measured on screen
  // rather than left to HERE's hit test, which lets the area itself swallow some of the small
  // handles' taps.
  midpointNear(event) {
    if (!this.closed || !this.map.geoToScreen) return null
    const { viewportX, viewportY } = event.currentPointer || {}
    if (viewportX === undefined) return null
    let nearest = null
    let nearestDistance = MIDPOINT_REACH_PX
    this.points.forEach((point, index) => {
      const screen = this.map.geoToScreen(
        midpoint(point, this.points[(index + 1) % this.points.length]),
      )
      const distance = Math.hypot(screen.x - viewportX, screen.y - viewportY)
      if (distance <= nearestDistance) {
        nearest = index
        nearestDistance = distance
      }
    })
    return nearest
  }

  geoAt(event) {
    const pointer = event.currentPointer
    return this.map.screenToGeo(pointer.viewportX, pointer.viewportY)
  }

  // ---- Dragging ---------------------------------------------------------------------------

  // HERE markers are not draggable out of the box: pause panning while the centre marker or a
  // corner is dragged and follow the pointer (the pattern of maps_controller.js).
  setupDragging() {
    this.map.addEventListener("dragstart", (event) => {
      if (this.draggable(event.target)) this.behavior.disable()
    })
    this.map.addEventListener("drag", (event) => {
      if (!this.draggable(event.target)) return
      const position = this.geoAt(event)
      if (event.target === this.marker) this.moveCenter(position)
      else this.movePoint(event.target.getData().index, position)
    })
    // enable() turns every feature back on, double-tap zoom included: switch it off again
    // while a shape is still being drawn.
    this.map.addEventListener("dragend", (event) => {
      if (!this.draggable(event.target)) return
      this.behavior.enable()
      this.updateDoubleTapZoom()
    })
  }

  draggable(target) {
    return target === this.marker || target?.getData?.()?.kind === "vertex"
  }

  // ---- Drawing aid ------------------------------------------------------------------------

  // data-action="change->geofence-editor#togglePositions"
  togglePositions(event) {
    this.showPositions(event.target.checked)
  }

  showPositions(visible) {
    if (!this.map) return
    if (visible && !this.positionsGroup) {
      const { H } = this
      this.positionsGroup = new H.map.Group()
      this.positionsGroup.addObjects(
        this.positionsValue.map(
          ([lat, lng]) =>
            new H.map.Circle({ lat, lng }, POSITION_RADIUS_M, { style: POSITION_STYLE }),
        ),
      )
      this.map.addObject(this.positionsGroup)
    } else if (!visible && this.positionsGroup) {
      this.map.removeObject(this.positionsGroup)
      this.positionsGroup = null
    }
  }
}

// Square metres up to a hectare, hectares up to a square kilometre, then km².
export function formatArea(squareMetres, format) {
  if (squareMetres < 10_000) return `${format.format(Math.round(squareMetres))} m²`
  if (squareMetres < 1_000_000) return `${format.format(squareMetres / 10_000)} ha`
  return `${format.format(squareMetres / 1_000_000)} km²`
}
